import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { RefreshCw, MessageCircle, Loader2, Star, AtSign, User, Send, MessageSquarePlus, Inbox, Columns3, Tag, AlertCircle, MailOpen, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { onRevalidationChange, isRevalidating } from '@/lib/cache/swr';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/empty-state';
import { useSettingsStore } from '@/stores/settings-store';
import { useConversationStore, normalizeConvId } from '@/stores/conversation-store';
import { useSenders } from '@/hooks/use-senders';
import { usePermissions } from '@/hooks/use-permissions';
import { ConversationSearch } from './conversation-search';
import { ConversationItem } from './conversation-item';
import { MentionsDialog } from './mentions-dialog';
import { WabaProfileView, getCachedProfilePic } from './waba-profile-view';
import type { Conversation, ConvChatLabel, Sender } from '@/types/messaging';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  searchQuery: string;
  filterLabel: string | null;
  filterAgent: string;
  chatLabels: Record<string, ConvChatLabel>;
  senderOptions: Array<{ value: string; label: string; description?: string }>;
  activeSenderId: string;
  onSenderChange: (value: string) => void;
  onSearchChange: (query: string) => void;
  onSearchByPhone: (phone: string) => void;
  phoneSearchActive: boolean;
  onFilterLabelChange: (label: string | null) => void;
  onFilterAgentChange: (agent: string) => void;
  /** Current "jump to" cutoff date (conversations last updated on or before this) */
  jumpBefore?: Date | null;
  /** Trigger a one-shot jump-load of conversations at the given date (null = reset) */
  onJumpToDate?: (date: Date | null) => void;
  /** Open the kanban (board) view, shown as an action in the empty state */
  onOpenKanban?: () => void;
  onSelectConversation: (conversation: Conversation) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  onNewChat?: () => void;
  mentionCount?: number;
  onSelectConversationFromMention?: (conv: Conversation) => void;
  /** The currently selected sender object (needed for WABA profile view) */
  activeSender?: Sender | null;
  /** Whether the channel is WhatsApp (enables profile icon) */
  isWhatsApp?: boolean;
  /** TPA module display name (e.g. "WhatsApp") for the Inbox title */
  tpaName?: string;
  /** Hide assignee and label filters (e.g. in CRM record context) */
  hideFilters?: boolean;
  /** Hide sender dropdown (e.g. during phone search) */
  hideSenderPicker?: boolean;
  /** Visible (permission-filtered) senders — conversations from senders NOT in this list are hidden */
  visibleSenders?: Sender[];
  className?: string;
}

export function ConversationList({
  jumpBefore,
  onJumpToDate,
  onOpenKanban,
  conversations,
  activeConversation,
  loading,
  loadingMore,
  hasMore,
  searchQuery,
  filterLabel,
  filterAgent,
  chatLabels,
  senderOptions,
  activeSenderId,
  onSenderChange,
  onSearchChange,
  onSearchByPhone,
  phoneSearchActive,
  onFilterLabelChange,
  onFilterAgentChange,
  onSelectConversation,
  onRefresh,
  onLoadMore,
  onNewChat,
  mentionCount,
  onSelectConversationFromMention,
  activeSender,
  isWhatsApp,
  tpaName,
  hideFilters,
  hideSenderPicker,
  visibleSenders,
  className,
}: ConversationListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const [showMentions, setShowMentions] = useState(false);
  const [showWabaProfile, setShowWabaProfile] = useState(false);
  const [revalidating, setRevalidating] = useState(() => isRevalidating('conversations:'));

  // Sender permission filtering — hide conversations from no_access senders.
  // Assignee visibility is enforced at the tab level: conversation-search hides
  // the "All" / "Unassigned" tabs based on inbox permissions, which sets the
  // Firestore assigneeEmail filter automatically.
  const { isAdmin, inboxPermissions } = usePermissions();
  /** Whether the "All" agent tab is actually on screen. conversation-search
   *  drops it for users without `canViewOtherUsersChats`, and hides the whole
   *  tab strip in CRM-record / phone-search context — so an empty state must
   *  not tell those users to switch to a tab they cannot see. */
  const canSwitchToAllTab = !hideFilters && !!inboxPermissions.canViewOtherUsersChats;
  const pinnedConversationIds = useSettingsStore((s) => s.pinnedConversationIds);

  // fetchConversations() already hydrates pinned chats missing from the page,
  // but settings load asynchronously at boot — on a cold start the pin list is
  // still empty when that first fetch runs. Re-run once the ids arrive (and on
  // any later pin change). The action no-ops when nothing is missing.
  const hydratePinnedConversations = useConversationStore((s) => s.hydratePinnedConversations);
  useEffect(() => {
    if (loading || pinnedConversationIds.length === 0) return;
    void hydratePinnedConversations();
  }, [pinnedConversationIds, loading, hydratePinnedConversations]);

  // Build a set of allowed sender phone numbers (digits-only) for fast lookup.
  // visibleSenders is already filtered by getSenderPermission in useSenders.
  // TODO: Optimise — for WhatsApp, push sender filtering to the Firestore query
  // level using `where('channelId', 'in', [...allowedMSISDNs])` when "All senders"
  // is selected. This avoids fetching hidden-sender conversations entirely, saving
  // bandwidth and Firestore reads. Caveats: Firestore `in` supports max 30 values,
  // and Twilio sender formats are inconsistent so client-side filtering stays for Twilio.
  const allowedSenderNumbers = useMemo(() => {
    if (isAdmin || !visibleSenders || visibleSenders.length === 0) return null;
    const nums = new Set<string>();
    for (const s of visibleSenders) {
      if (s.phoneNumber) nums.add(s.phoneNumber.replace(/\D/g, ''));
      // Also add the sender ID itself (WhatsApp phone_number_id)
      if (s.id) nums.add(s.id);
    }
    return nums.size > 0 ? nums : null;
  }, [visibleSenders, isAdmin]);

  const filteredConversations = useMemo(() => {
    const visible = !allowedSenderNumbers
      ? conversations
      : conversations.filter((conv) => {
        // WhatsApp: channelId in metadata = sender MSISDN
        const channelId = conv.metadata?.channelId as string | undefined;
        // Twilio: from in metadata = sender phone number
        const fromNumber = conv.metadata?.from as string | undefined;
        const senderNum = (channelId || fromNumber || '').replace(/\D/g, '');
        if (senderNum && !allowedSenderNumbers.has(senderNum)) {
          return false;
        }
        return true;
      });

    // Float pinned rows to the top. Everything else keeps the order the store
    // gave us (queries already come back newest-first, and phone search has its
    // own deliberate merge order) — this only lifts the pinned ones out, so an
    // unpinned chat drops straight back to the slot it already had.
    if (pinnedConversationIds.length === 0 || phoneSearchActive) return visible;

    const pinnedSet = new Set(pinnedConversationIds.map(normalizeConvId));
    const isPinned = (c: Conversation) => pinnedSet.has(normalizeConvId(c.id));
    const pinned = visible.filter(isPinned);
    if (pinned.length === 0) return visible;

    return [...pinned, ...visible.filter((c) => !isPinned(c))];
  }, [conversations, allowedSenderNumbers, pinnedConversationIds, phoneSearchActive]);

  // Keep latest loadingMore/hasMore/onLoadMore in a ref so the IntersectionObserver
  // doesn't need to re-attach on every state change
  const loadStateRef = useRef({ loadingMore, hasMore, onLoadMore });
  loadStateRef.current = { loadingMore, hasMore, onLoadMore };

  // IntersectionObserver-based infinite scroll — more reliable than scroll events.
  // Fires continuously while the sentinel is visible, so fast scrolls correctly trigger
  // subsequent loads after the first batch completes.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        const { loadingMore: lm, hasMore: hm, onLoadMore: olm } = loadStateRef.current;
        if (!lm && hm) olm();
      },
      { root, rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [conversations.length]);

  // Retrigger loadMore after a load completes if sentinel is still visible
  // (handles the case where fast scroll landed past the trigger during load)
  useEffect(() => {
    if (loadingMore || !hasMore) return;
    const sentinel = loadMoreSentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;
    const rootRect = root.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    // If sentinel is within or above the visible area, trigger another load
    if (sentinelRect.top < rootRect.bottom + 200) {
      onLoadMore();
    }
  }, [loadingMore, hasMore, onLoadMore]);

  useEffect(() => {
    return onRevalidationChange((keys) => {
      let active = false;
      for (const k of keys) { if (k.startsWith('conversations:')) { active = true; break; } }
      setRevalidating(active);
    });
  }, []);

  // Close profile view when sender changes
  useEffect(() => {
    const t = setTimeout(() => setShowWabaProfile(false), 0);
    return () => clearTimeout(t);
  }, [activeSenderId]);

  // Scroll handler kept as a fallback (the IntersectionObserver is the primary trigger)
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom) {
      onLoadMore();
    }
  }, [loadingMore, hasMore, onLoadMore]);

  // Show profile icon only when a specific WABA number is selected (not "All")
  const showProfileIcon = isWhatsApp && activeSender && activeSenderId !== '__all__';
  const cachedProfilePic = showProfileIcon ? getCachedProfilePic(activeSenderId) : null;

  // When profile is open, render profile view instead of conversation list
  if (showWabaProfile && activeSender) {
    return (
      <div className={cn('flex flex-col bg-background dark:bg-card overflow-hidden', className)}>
        <WabaProfileView
          sender={activeSender}
          onBack={() => setShowWabaProfile(false)}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col bg-background dark:bg-card relative',
        // Low-height windows (<700px): scroll the whole pane so header is reachable.
        // Normal heights: header sticky, only list scrolls.
        'overflow-hidden [@media(max-height:699px)]:overflow-y-auto [@media(max-height:699px)]:scrollbar-thin',
        className
      )}
    >
      {/* Header */}
      <div className="shrink-0 px-3 pt-2.5 pb-2 border-b border-border/60 bg-muted/40 dark:bg-card">
        <p data-testid="inbox-heading" className="mb-1.5 text-[16px] leading-5 font-semibold text-foreground">{isWhatsApp ? 'WABA Inbox' : tpaName ? `${tpaName} Inbox` : 'Inbox'}</p>
        {!hideSenderPicker && <div className="flex items-center gap-2">
          {/* Profile icon — shown when a specific WABA number is selected */}
          {showProfileIcon && (
            <button
              onClick={() => setShowWabaProfile(true)}
              className={cn(
                'shrink-0 h-7 w-7 rounded-full flex items-center justify-center transition-colors overflow-hidden',
                cachedProfilePic
                  ? 'ring-2 ring-emerald-500/30 hover:ring-emerald-500/60'
                  : 'bg-emerald-600 text-white hover:bg-emerald-600/90'
              )}
              title={`View ${activeSender.data?.verifiedName || activeSender.label} profile`}
            >
              {cachedProfilePic ? (
                <img
                  src={cachedProfilePic}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
              ) : null}
              <User className={cn('h-3.5 w-3.5', cachedProfilePic ? 'hidden' : '')} />
            </button>
          )}
          {/* When only 1 real sender exists, show static label instead of dropdown */}
          {senderOptions.length <= 2 && senderOptions.length > 1 ? (
            <div data-testid="inbox-sender-picker" className="flex-1 min-w-0 flex items-center gap-2 h-8 rounded-md border border-input bg-background px-2.5 text-sm">
              <span className="truncate font-medium">{senderOptions[1].label}</span>
              {senderOptions[1].description && (
                <span className="text-xs text-muted-foreground truncate">{senderOptions[1].description}</span>
              )}
            </div>
          ) : (
            // Wrapper div needed for data-testid because SenderSelectWithStar
            // and its inner Select component don't forward rest props to the DOM
            <div data-testid="inbox-sender-picker" className="flex-1 min-w-0">
              <SenderSelectWithStar
                value={activeSenderId}
                onValueChange={onSenderChange}
                options={senderOptions}
                viewContext="inbox"
              />
            </div>
          )}
        </div>}
      </div>

      {/* Search + Filters */}
      <ConversationSearch
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        onSearchByPhone={onSearchByPhone}
        phoneSearchActive={phoneSearchActive}
        filterLabel={filterLabel}
        onFilterLabelChange={onFilterLabelChange}
        filterAgent={filterAgent}
        onFilterAgentChange={onFilterAgentChange}
        chatLabels={chatLabels}
        onNewChat={onNewChat}
        mentionCount={mentionCount}
        mentionsPanel={
          <>
            <button
              data-testid="mentions-btn"
              onClick={() => setShowMentions(!showMentions)}
              className={cn(
                'rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors relative',
                showMentions && 'bg-muted text-foreground'
              )}
              title="Mentions"
            >
              <AtSign className="h-4 w-4" />
              {!!mentionCount && mentionCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-500 text-[9px] font-bold text-white px-1">
                  {mentionCount > 99 ? '99+' : mentionCount}
                </span>
              )}
            </button>
            <MentionsDialog
              open={showMentions}
              onOpenChange={setShowMentions}
              onOpenConversation={(conv) => {
                setShowMentions(false);
                (onSelectConversationFromMention || onSelectConversation)(conv);
              }}
            />
          </>
        }
        onRefresh={onRefresh}
        loading={loading}
        hideAssigneeFilters={hideFilters}
        hideLabelFilters={hideFilters}
        jumpBefore={jumpBefore ?? null}
        onJumpToDate={onJumpToDate}
      />

      {/* Conversation list — scrolls independently on normal heights,
          part of outer scroll on low heights */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto scrollbar-thin [@media(max-height:699px)]:overflow-visible"
      >
        {loading && filteredConversations.length === 0 ? (
          <ConversationListSkeleton />
        ) : filteredConversations.length === 0 ? (
          searchQuery ? (
            <EmptyState
              icon={<MessageCircle className="h-8 w-8 text-muted-foreground" />}
              title="No results"
              description="Try a different search term"
            />
          ) : filterLabel ? (
            // Filter-aware empty state — explains why nothing is shown for
            // the current label filter and how to populate it.
            (() => {
              let icon = <Tag className="h-7 w-7 text-primary/60" />;
              let title = 'No conversations match';
              let description = 'No conversations match the current filter.';

              // The agent tab narrows the list too, so an empty result is
              // often down to that rather than the label. Name the tab, or
              // the label alone looks broken. Only mention the "All" tab when
              // it is actually on screen (permissions / CRM-record context can
              // remove it) — otherwise we send users to a control they can't
              // see.
              const agentTab =
                filterAgent === 'mine' ? 'Mine'
                  : filterAgent === 'unassigned' ? 'Unassigned'
                    : filterAgent && filterAgent !== 'all' ? filterAgent
                      : null;
              const widenHint = agentTab && canSwitchToAllTab
                ? ' Switch to the "All" tab to include every chat.'
                : '';

              if (filterLabel === '__unread__') {
                icon = <MailOpen className="h-7 w-7 text-primary/60" />;
                title = 'No unread conversations';
                description = agentTab
                  ? `Nothing unread in the "${agentTab}" tab.${widenHint}`
                  : 'You\'re all caught up. New unread chats will appear here as messages come in.';
              } else if (filterLabel === '__send_failed__') {
                icon = <AlertCircle className="h-7 w-7 text-red-500/70" />;
                title = 'No failed sends';
                description = agentTab
                  ? `No failed sends in the "${agentTab}" tab.${widenHint}`
                  : 'If a message fails to deliver, the conversation will show up here so you can retry it.';
              } else {
                const labelDisplay = chatLabels[filterLabel]?.display_label || filterLabel;
                title = `No conversations labeled "${labelDisplay}"`;
                description = agentTab
                  ? `No chats in the "${agentTab}" tab have the "${labelDisplay}" label.${widenHint}`
                  : `Assign the "${labelDisplay}" label to any chat from the chat header to list it here.`;
              }

              return (
                <div className="flex flex-col items-center justify-center gap-5 py-12 px-6 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/10 to-purple-500/10 border border-primary/10">
                    {icon}
                  </div>
                  <div className="space-y-2 max-w-[260px]">
                    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
                  </div>
                  <button
                    onClick={() => onFilterLabelChange(null)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <X className="h-3 w-3" />
                    Clear filter
                  </button>
                </div>
              );
            })()
          ) : (
            <div className="flex flex-col items-center justify-center gap-5 py-12 px-6 text-center">
              {/* Decorative icon cluster */}
              <div className="relative">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/10 to-purple-500/10 border border-primary/10">
                  <Inbox className="h-7 w-7 text-primary/60" />
                </div>
                <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-muted border-2 border-card">
                  <MessageSquarePlus className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
              </div>

              {/* Text — with no label filter the agent tab is the only thing
                  narrowing the list, so say so instead of implying the inbox
                  is empty. */}
              <div className="space-y-2 max-w-[220px]">
                <h3 className="text-sm font-semibold text-foreground">
                  {filterAgent === 'mine' ? 'No conversations in "Mine"'
                    : filterAgent === 'unassigned' ? 'No conversations in "Unassigned"'
                      : filterAgent && filterAgent !== 'all' ? `No conversations for ${filterAgent}`
                        : 'No conversations yet'}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {filterAgent && filterAgent !== 'all' && canSwitchToAllTab
                    ? 'Switch to the "All" tab to see every conversation.'
                    : activeSender && activeSenderId !== '__all__'
                      ? <>Send a message to <strong className="text-foreground font-medium">{activeSender.label}</strong> to see it here</>
                      : 'Send or receive a message to get started. Conversations will appear here automatically.'
                  }
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col items-center gap-2">
                {onNewChat && (
                  <button
                    onClick={onNewChat}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
                  >
                    <Send className="h-3 w-3" />
                    New Conversation
                  </button>
                )}
                {onOpenKanban && (
                  <button
                    onClick={onOpenKanban}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <Columns3 className="h-3 w-3" />
                    Open Kanban View
                  </button>
                )}
                <button
                  onClick={onRefresh}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RefreshCw className="h-3 w-3" />
                  Refresh
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="divide-y divide-border/40">
            {revalidating && (
              <div className="flex justify-center py-1.5 border-b border-border/40">
                <div className="h-4 w-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
              </div>
            )}
            {filteredConversations.map((conv) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isActive={activeConversation?.id === conv.id}
                onClick={() => onSelectConversation(conv)}
              />
            ))}
          </div>
        )}

        {/* Load more zone — 100px tall; triggers load-on-scroll and shows spinner while loading.
            When a client-side filter (agent pill / Unassigned) drops the whole page, keep a
            manual "Load more" so later pages stay reachable — but without the observer, so
            an empty view doesn't auto-fetch page after page on its own. */}
        {hasMore && (
          <div
            ref={conversations.length > 0 ? loadMoreSentinelRef : undefined}
            className="flex items-center justify-center border-t border-border/40"
            style={{ height: '100px' }}
          >
            {loadingMore || loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <button
                onClick={onLoadMore}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 hover:underline transition-colors"
              >
                Load more conversations
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const ALL_SENDERS_FAKE_ID = '__all__';

/** Sender select with inline star icon to set default for a view */
function SenderSelectWithStar({
  value,
  onValueChange,
  options,
  viewContext,
}: {
  value: string;
  onValueChange: (v: string) => void;
  options: Array<{ value: string; label: string; description?: string }>;
  viewContext: 'inbox' | 'chat' | 'compose';
}) {
  const preferences = useSettingsStore((s) => s.preferences);
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);
  const { allSenders } = useSenders(viewContext);

  const getDefaultId = () => {
    if (viewContext === 'inbox') return preferences.inboxDefaultSender?.id;
    if (viewContext === 'chat') return preferences.chatDefaultSender?.id;
    return preferences.composeDefaultSender?.id;
  };

  const defaultId = getDefaultId();

  const defaultSenderObjKey = viewContext === 'inbox'
    ? 'inboxDefaultSender' as const
    : viewContext === 'chat'
      ? 'chatDefaultSender' as const
      : 'composeDefaultSender' as const;

  const handleStarClick = (e: React.MouseEvent, senderId: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (defaultId === senderId) {
      updatePreferences({ [defaultSenderObjKey]: undefined });
    } else {
      const s = allSenders.find((x) => x.id === senderId);
      if (s) {
        updatePreferences({
          [defaultSenderObjKey]: { id: s.id, label: s.label, channel: s.channel, phoneNumber: s.phoneNumber },
        });
      }
    }
  };

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      options={options}
      placeholder={options[0]?.label || 'All senders'}
      searchable={options.length > 5}
      className="flex-1 min-w-0"
      renderOption={(option, isSelected) => {
        const isAllSenders = option.value === ALL_SENDERS_FAKE_ID;
        const isDefault = defaultId === option.value;

        return (
          <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
            <div className="flex flex-col items-start flex-1 min-w-0">
              <span className={cn('font-medium truncate', isSelected && 'text-primary')}>
                {option.label}
              </span>
              {option.description && (
                <span className="text-[10px] text-muted-foreground truncate">
                  {option.description}
                </span>
              )}
            </div>
            {!isAllSenders && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => handleStarClick(e, option.value)}
                className={cn(
                  'shrink-0 rounded p-0.5 transition-colors cursor-pointer',
                  isDefault
                    ? 'text-amber-500'
                    : 'text-muted-foreground/30 hover:text-amber-500'
                )}
                title={isDefault ? 'Remove default' : 'Set as default for this view'}
              >
                <Star className={cn('h-3 w-3', isDefault && 'fill-amber-500')} />
              </span>
            )}
          </div>
        );
      }}
    />
  );
}

function ConversationListSkeleton() {
  return (
    <div className="divide-y divide-border/30">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-3">
          {/* Avatar — matches h-10 w-10 rounded-full in ConversationItem */}
          <Skeleton className="h-10 w-10 rounded-full shrink-0" />
          {/* Content — matches the 2-row layout in ConversationItem */}
          <div className="flex-1 min-w-0 space-y-1.5">
            {/* Row 1: name + time */}
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-3 w-14 shrink-0" />
            </div>
            {/* Row 2: message preview */}
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}
